import React from 'react';
import { Home, ArrowLeft, Phone, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const NotFound: React.FC = () => {
  const navigate = useNavigate();

  const suggestions = [
    { icon: Home, text: "Retour à l'accueil", action: () => navigate('/') },
    { icon: Search, text: "Voir nos réalisations", action: () => navigate('/#realisations') },
    { icon: Phone, text: "Nous contacter", action: () => navigate('/#contact') },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 via-sage-50 to-wood-50 flex items-center justify-center p-4">
      <div className="max-w-4xl w-full">
        <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
          <div className="grid grid-cols-1 md:grid-cols-2">
            {/* Image de Jérémy */}
            <div className="relative bg-gradient-to-br from-wood-100 to-sage-100 p-8 flex items-center justify-center">
              <div className="relative">
                <img 
                  src="/images/jeremy2.png" 
                  alt="Jérémy ARS - Artisan menuisier perplexe"
                  className="w-full max-w-sm rounded-2xl shadow-xl"
                />
                {/* Bulle de pensée */}
                <div className="absolute -top-4 -right-4 bg-white rounded-full p-6 shadow-lg animate-bounce">
                  <span className="text-4xl">🤔</span>
                </div>
              </div>
            </div>

            {/* Contenu */}
            <div className="p-8 md:p-12 flex flex-col justify-center">
              {/* Code 404 stylisé */}
              <div className="mb-6">
                <div className="flex items-center space-x-2 mb-4">
                  <div className="text-8xl font-bold text-wood-600">4</div>
                  <div className="text-8xl font-bold text-sage-600">0</div>
                  <div className="text-8xl font-bold text-wood-600">4</div>
                </div>
              </div>

              {/* Message drôle */}
              <div className="space-y-4 mb-8">
                <h1 className="text-3xl font-bold text-stone-900">
                  Oups ! Même Jérémy ne trouve pas cette page...
                </h1>
                <p className="text-lg text-stone-600 leading-relaxed">
                  Et pourtant, il a cherché dans tous les coins et recoins de l'atelier ! 
                  Cette page semble avoir été aussi bien cachée qu'un outil oublié derrière une étagère.
                </p>
                <div className="bg-wood-50 border-l-4 border-wood-600 p-4 rounded">
                  <p className="text-sm text-stone-700">
                    <strong>💡 Le saviez-vous ?</strong> En menuiserie, on mesure deux fois 
                    avant de couper. Sur internet, on vérifie l'URL avant de cliquer !
                  </p>
                </div>
              </div>

              {/* Actions suggérées */}
              <div className="space-y-3">
                <p className="text-sm font-semibold text-stone-700 uppercase tracking-wide">
                  Que diriez-vous de...
                </p>
                {suggestions.map((suggestion, index) => (
                  <button
                    key={index}
                    onClick={suggestion.action}
                    className="w-full bg-stone-100 hover:bg-wood-600 hover:text-white text-stone-800 px-6 py-4 rounded-xl font-medium transition-all duration-200 flex items-center space-x-3 group"
                  >
                    <suggestion.icon className="w-5 h-5 group-hover:scale-110 transition-transform" />
                    <span>{suggestion.text}</span>
                  </button>
                ))}
              </div>

              {/* Contact d'urgence */}
              <div className="mt-8 pt-6 border-t border-stone-200">
                <p className="text-sm text-stone-500 text-center">
                  Besoin d'aide ? Appelez Jérémy au{' '}
                  <a href="tel:+33676825228" className="text-wood-600 hover:text-wood-700 font-semibold">
                    +33 6 76 82 52 28
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Citation humoristique */}
        <div className="mt-8 text-center">
          <blockquote className="text-stone-600 italic">
            "Le bois se travaille avec patience, les pages 404 se trouvent avec persévérance !"
          </blockquote>
          <p className="text-sm text-stone-500 mt-2">- Jérémy ARS, philosophe menuisier</p>
        </div>

        {/* Bouton retour alternatif */}
        <div className="mt-6 text-center">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center space-x-2 text-stone-600 hover:text-wood-600 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Revenir en arrière</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default NotFound;
